import { useState } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import { Timer, Play, Pause, RotateCcw, X, Check } from 'lucide-react';
import { useFocusTimer } from '../hooks/useFocusTimer';

export default function FocusTimer() {
  const { preferences } = usePreferences();
  const [isOpen, setIsOpen] = useState(false);
  const {
    isRunning, timeLeft, totalTime, sessions,
    progress, minutes, seconds,
    toggle, reset, selectDuration,
  } = useFocusTimer(preferences.focusBlockMinutes || 25);

  const circumference = 2 * Math.PI * 36;
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="relative">
      <button data-testid="focus-timer-toggle" onClick={() => setIsOpen((p) => !p)} className="pd-icon-button relative" title="Focus Timer">
        <Timer className="w-[17px] h-[17px]" />
        {isRunning && <div className="absolute top-1.5 right-1.5 w-[7px] h-[7px] rounded-full animate-pulse" style={{ background: 'var(--pd-success)' }} />}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={() => setIsOpen(false)} />
          <div data-testid="focus-timer-panel" className="absolute right-0 top-14 w-72 rounded-[22px] z-[201] animate-fade-in overflow-hidden"
            style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>

            <div className="px-4 pt-4 pb-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--pd-border)' }}>
              <div>
                <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>Focus Timer</div>
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>{sessions} session{sessions !== 1 ? 's' : ''} today</div>
              </div>
              <button data-testid="focus-timer-close" onClick={() => setIsOpen(false)} className="p-1 rounded-lg" style={{ color: 'var(--pd-muted)' }}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col items-center py-5">
              <div className="relative">
                <svg width="88" height="88" className="-rotate-90">
                  <circle cx="44" cy="44" r="36" fill="none" stroke="var(--pd-border)" strokeWidth="5" />
                  <circle cx="44" cy="44" r="36" fill="none"
                    stroke={timeLeft === 0 ? 'var(--pd-success)' : isRunning ? 'var(--pd-focus)' : 'var(--pd-muted)'}
                    strokeWidth="5" strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span data-testid="focus-timer-display" className="text-[22px] font-semibold tabular-nums" style={{ color: 'var(--pd-text)', letterSpacing: '-0.04em' }}>
                    {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                  </span>
                </div>
              </div>

              {timeLeft === 0 && (
                <div data-testid="focus-timer-done" className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'var(--pd-green-soft)', color: 'var(--pd-success)' }}>
                  <Check className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-semibold">Session complete</span>
                </div>
              )}

              <div className="flex items-center gap-2 mt-4">
                <button data-testid="focus-timer-start" onClick={toggle}
                  className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
                  style={{ background: isRunning ? 'var(--pd-warning)' : 'var(--pd-focus)' }}>
                  {isRunning
                    ? <><Pause className="w-3.5 h-3.5 inline mr-1" />Pause</>
                    : timeLeft === 0
                    ? <><Play className="w-3.5 h-3.5 inline mr-1" />Again</>
                    : <><Play className="w-3.5 h-3.5 inline mr-1" />{timeLeft < totalTime ? 'Resume' : 'Start'}</>
                  }
                </button>
                <button data-testid="focus-timer-reset" onClick={reset} className="p-2 rounded-xl btn-ghost">
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="px-4 pb-4">
              <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2" style={{ color: 'var(--pd-subtle)' }}>Duration</div>
              <div className="flex gap-1.5">
                {[15, 25, 30, 45, 50].map((m) => (
                  <button key={m} data-testid={`focus-timer-duration-${m}`} onClick={() => selectDuration(m)}
                    className="flex-1 py-2 rounded-xl text-[11px] font-medium transition-colors"
                    style={{
                      background: Math.floor(totalTime / 60) === m ? 'var(--pd-focus)' : 'var(--pd-panel-2)',
                      color: Math.floor(totalTime / 60) === m ? 'white' : 'var(--pd-muted)',
                      border: '1px solid var(--pd-border)',
                    }}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
